import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import {
  approveProjectDonationPayment,
  cancelProjectDonation,
  fetchAllProjectDonations,
  fetchProjectDonationById,
  fetchProjectDonationStatistics,
  fetchProjectDonationSummaryByProject,
} from '../../controllers/paymentsController';
import { fetchAllProjects } from '../../controllers/projectsController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import { resolvePaymentsFileUrl } from '../../api/httpClientPayments';
import {
  ProjectDonationStatus,
  ProjectDonationStatusNames,
  ProjectPaymentMethodNames,
  getProjectDonationStatusChipSx,
} from './projectDonationConstants';
import ProjectDonationLogoInfoSection from './components/ProjectDonationLogoInfoSection';
import CreateProjectDonationDialog from './components/CreateProjectDonationDialog';
import { formatMoneyWithCurrency } from '../../utils/currencyDisplay';

const cardSx = {
  border: '1px solid #e7e7e7',
  boxShadow: 'none',
  borderRadius: '8px',
};

const tableHeadCellSx = {
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { py: 1, px: 1.5, fontSize: '14px' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR');
};

const formatMoney = formatMoneyWithCurrency;

const ProjectPayments = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const [stats, setStats] = useState(null);
  const [summaryByProject, setSummaryByProject] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [approvePaymentDate, setApprovePaymentDate] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statistics, summary] = await Promise.all([
        fetchProjectDonationStatistics(),
        fetchProjectDonationSummaryByProject(),
      ]);
      setStats(statistics || null);
      setSummaryByProject(Array.isArray(summary) ? summary : []);
    } catch {
      setStats(null);
      setSummaryByProject([]);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await fetchAllProjects({ pageNumber: 1, pageSize: 200 });
      setProjects(Array.isArray(data) ? data : []);
    } catch {
      setProjects([]);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data, meta } = await fetchAllProjectDonations({
        pageNumber: page,
        pageSize,
        status: status === '' ? null : Number(status),
        projectId: selectedProjectId || null,
        searchTerm: searchTerm || null,
      });
      setRows(Array.isArray(data) ? data : []);
      setTotalPages(meta?.totalPages || 1);
    } catch (e) {
      toast.error(e?.message || 'Proje bağışları yüklenemedi');
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, selectedProjectId, searchTerm]);

  useEffect(() => {
    loadProjects();
    loadStats();
  }, [loadProjects, loadStats]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openDetail = useCallback(async (row) => {
    if (!row?.id) return;
    setSelectedRow(row);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await fetchProjectDonationById(row.id);
      setDetail(data);
      setSelectedRow(data);
    } catch (e) {
      toast.error(e?.message || 'Detay yüklenemedi');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    const donationId = searchParams.get('donationId');
    if (!donationId) return;
    openDetail({ id: donationId });
    const next = new URLSearchParams(searchParams);
    next.delete('donationId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, openDetail]);

  const activeItem = detail || selectedRow;

  const canApprove = useMemo(
    () => activeItem?.status === ProjectDonationStatus.AwaitingApproval,
    [activeItem],
  );

  const canCancel = useMemo(
    () => activeItem
      && activeItem.status !== ProjectDonationStatus.Paid
      && activeItem.status !== ProjectDonationStatus.Cancelled,
    [activeItem],
  );

  const handleApprove = async () => {
    const target = detail || selectedRow;
    if (!target) return;
    setProcessing(true);
    try {
      await approveProjectDonationPayment({
        donationId: target.id,
        paymentDate: approvePaymentDate ? new Date(approvePaymentDate).toISOString() : new Date().toISOString(),
        notes: approveNotes?.trim() || null,
      });
      toast.success('Proje bağışı onaylandı');
      setApproveOpen(false);
      setDetailOpen(false);
      setApproveNotes('');
      setApprovePaymentDate('');
      loadRows();
      loadStats();
    } catch (e) {
      toast.error(e?.message || 'Onaylanamadı');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    const target = detail || selectedRow;
    if (!target) return;
    setProcessing(true);
    try {
      await cancelProjectDonation({
        donationId: target.id,
        cancellationReason: cancelReason?.trim() || null,
      });
      toast.success('Proje bağışı iptal edildi');
      setCancelOpen(false);
      setDetailOpen(false);
      setCancelReason('');
      loadRows();
      loadStats();
    } catch (e) {
      toast.error(e?.message || 'İptal edilemedi');
    } finally {
      setProcessing(false);
    }
  };

  const selectedProjectSummary = selectedProjectId
    ? summaryByProject.find((s) => s.projectId === selectedProjectId)
    : null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#444444' }}>
          Proje Bağışları
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ textTransform: 'none', backgroundColor: '#0E4D94', boxShadow: 'none' }}
        >
          Manuel Bağış
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, fontSize: '13px' }}>
        PayTR kredi kartı ödemelerinde Logo borç (04) ve tahsilat (08) fişleri otomatik oluşur.
        Admin nakit/havale kayıtları Logo üretmez.
      </Alert>

      <Card sx={{ mb: 2, ...cardSx }}>
        <CardContent sx={{ py: 2, px: 3 }}>
          {statsLoading && !stats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : stats ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
              <Box sx={{ minWidth: 120 }}>
                <Typography sx={{ fontSize: 12, color: '#a3a3a3', mb: 0.5 }}>Toplam Bağış</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{stats.totalDonationCount}</Typography>
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <Typography sx={{ fontSize: 12, color: '#a3a3a3', mb: 0.5 }}>Ödenen</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#17C653' }}>{stats.paidDonationCount}</Typography>
              </Box>
              <Box
                sx={{ minWidth: 120, cursor: stats.awaitingApprovalCount > 0 ? 'pointer' : 'default' }}
                onClick={() => {
                  if (stats.awaitingApprovalCount > 0) {
                    setStatus(String(ProjectDonationStatus.AwaitingApproval));
                    setPage(1);
                  }
                }}
              >
                <Typography sx={{ fontSize: 12, color: '#a3a3a3', mb: 0.5 }}>Onay Bekleyen</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FF9800' }}>
                    {stats.awaitingApprovalCount}
                  </Typography>
                  {stats.awaitingApprovalCount > 0 && (
                    <Chip label="Kuyruğu göster" size="small" color="warning" sx={{ height: 22, fontSize: '11px' }} />
                  )}
                </Stack>
              </Box>
              <Box sx={{ minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#a3a3a3', mb: 0.5 }}>Toplam Tutar</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{formatMoney(stats.totalAmount, stats.currency)}</Typography>
              </Box>
              <Box sx={{ minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#a3a3a3', mb: 0.5 }}>Ödenen Tutar</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#17C653' }}>
                  {formatMoney(stats.paidAmount, stats.currency)}
                </Typography>
              </Box>
              {selectedProjectSummary && (
                <Box sx={{ minWidth: 140 }}>
                  <Typography sx={{ fontSize: 12, color: '#a3a3a3', mb: 0.5 }}>Seçili Proje (Paid)</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#0E4D94' }}>
                    {formatMoney(selectedProjectSummary.totalAmount, selectedProjectSummary.currency)}
                  </Typography>
                </Box>
              )}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 14, color: '#a3a3a3' }}>İstatistikler yüklenemedi.</Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, ...cardSx }}>
        <CardContent sx={{ py: 2, px: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <Box sx={{ minWidth: 200, flex: 1 }}>
              <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Proje</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={selectedProjectId}
                onChange={(e) => { setSelectedProjectId(e.target.value); setPage(1); }}
                sx={textFieldSx}
              >
                <MenuItem value="">Tümü</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ minWidth: 180 }}>
              <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Durum</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                sx={textFieldSx}
              >
                <MenuItem value="">Tümü</MenuItem>
                {Object.entries(ProjectDonationStatusNames).map(([v, label]) => (
                  <MenuItem key={v} value={v}>{label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ minWidth: 220, flex: 1 }}>
              <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Ara (açıklama / referans)</Typography>
              <TextField
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                placeholder="Açıklama veya dekont no..."
                sx={textFieldSx}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f5f5f5', borderRadius: '8px', py: 1, px: 2.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadCellSx}>Tarih</TableCell>
              <TableCell sx={tableHeadCellSx}>Proje</TableCell>
              <TableCell sx={tableHeadCellSx}>Bağışçı</TableCell>
              <TableCell sx={tableHeadCellSx}>Tutar</TableCell>
              <TableCell sx={tableHeadCellSx}>Durum</TableCell>
              <TableCell sx={tableHeadCellSx}>Ödeme</TableCell>
              <TableCell align="right" sx={tableHeadCellSx}>İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#a3a3a3' }}>
                  Kayıt bulunamadı
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const chipSx = getProjectDonationStatusChipSx(row.status);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(row)}
                  >
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{row.projectName || row.projectId}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar src={resolveGroupsAssetUrl(row.payerProfilePictureUrl)} sx={{ width: 28, height: 28 }} />
                        <Typography sx={{ fontSize: 14 }}>{row.payerFullName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatMoney(row.amount, row.currency)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.statusName || ProjectDonationStatusNames[row.status]}
                        sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', ...chipSx }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '13px' }}>{row.paymentMethodName || '-'}</Typography>
                      <Typography sx={{ fontSize: '12px', color: '#a3a3a3' }}>{formatDateTime(row.paymentDate)}</Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Detay">
                          <IconButton size="small" onClick={() => openDetail(row)}>
                            <VisibilityIcon sx={{ fontSize: 18, color: '#0E4D94' }} />
                          </IconButton>
                        </Tooltip>
                        {row.status === ProjectDonationStatus.AwaitingApproval && (
                          <Tooltip title="Onayla">
                            <IconButton
                              size="small"
                              onClick={() => { setSelectedRow(row); setApproveOpen(true); }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 18, color: '#17C653' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.status !== ProjectDonationStatus.Paid && row.status !== ProjectDonationStatus.Cancelled && (
                          <Tooltip title="İptal">
                            <IconButton
                              size="small"
                              onClick={() => { setSelectedRow(row); setCancelOpen(true); }}
                            >
                              <CancelIcon sx={{ fontSize: 18, color: '#F8285A' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} size="small" />
        </Box>
      )}

      {/* Detay */}
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setDetail(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', pr: 3 }}>
          Proje Bağışı Detay
          <IconButton onClick={() => { setDetailOpen(false); setDetail(null); }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Proje</Typography>
                  {detail.projectId ? (
                    <Link component={RouterLink} to={`/projects/${detail.projectId}`} sx={{ fontSize: '14px', fontWeight: 600 }}>
                      {detail.projectName || detail.projectId}
                    </Link>
                  ) : (
                    <Typography sx={{ fontSize: '14px' }}>{detail.projectName || '-'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Bağışçı</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={resolveGroupsAssetUrl(detail.payerProfilePictureUrl)} sx={{ width: 32, height: 32 }} />
                    {detail.payerPersonId ? (
                      <Link
                        component={RouterLink}
                        to={`/members/${detail.payerPersonId}`}
                        sx={{ fontSize: '14px', fontWeight: 500 }}
                      >
                        {detail.payerFullName}
                      </Link>
                    ) : (
                      <Typography sx={{ fontSize: '14px' }}>{detail.payerFullName}</Typography>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Tutar</Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#0E4D94' }}>
                    {formatMoney(detail.amount, detail.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Durum</Typography>
                  <Chip
                    size="small"
                    label={detail.statusName || ProjectDonationStatusNames[detail.status]}
                    sx={{ ...getProjectDonationStatusChipSx(detail.status), fontWeight: 600 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Ödeme yöntemi</Typography>
                  <Typography sx={{ fontSize: '14px' }}>
                    {detail.paymentMethodName || ProjectPaymentMethodNames[detail.paymentMethod] || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Ödeme tarihi</Typography>
                  <Typography sx={{ fontSize: '14px' }}>{formatDateTime(detail.paymentDate)}</Typography>
                </Grid>
                {detail.referenceNumber && (
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Referans</Typography>
                    <Typography sx={{ fontSize: '14px', fontFamily: 'monospace' }}>{detail.referenceNumber}</Typography>
                  </Grid>
                )}
                {detail.description && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Mesaj</Typography>
                    <Typography sx={{ fontSize: '14px' }}>{detail.description}</Typography>
                  </Grid>
                )}
                {detail.paymentProofUrl && (
                  <Grid item xs={12}>
                    <Button
                      href={resolvePaymentsFileUrl(detail.paymentProofUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ textTransform: 'none' }}
                    >
                      Ödeme kanıtını görüntüle
                    </Button>
                  </Grid>
                )}
              </Grid>

              {(detail.payTrMerchantOid || detail.payTrPaymentType) && (
                <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
                  <CardContent>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 1 }}>PayTR</Typography>
                    <Stack spacing={0.5}>
                      {detail.payTrMerchantOid && (
                        <Typography sx={{ fontSize: '13px' }}>
                          Sipariş: <Box component="span" sx={{ fontFamily: 'monospace' }}>{detail.payTrMerchantOid}</Box>
                        </Typography>
                      )}
                      {detail.payTrPaymentType && (
                        <Typography sx={{ fontSize: '13px' }}>Tip: {detail.payTrPaymentType}</Typography>
                      )}
                      {detail.payTrCallbackReceivedAt && (
                        <Typography sx={{ fontSize: '13px' }}>
                          Callback: {formatDateTime(detail.payTrCallbackReceivedAt)}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <ProjectDonationLogoInfoSection detail={detail} />
            </Stack>
          ) : (
            <Alert severity="error">Detay yüklenemedi</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDetailOpen(false); setDetail(null); }} sx={{ textTransform: 'none' }}>Kapat</Button>
          {canCancel && (
            <Button
              color="warning"
              onClick={() => { setDetailOpen(false); setCancelOpen(true); }}
              sx={{ textTransform: 'none' }}
            >
              İptal
            </Button>
          )}
          {canApprove && (
            <Button
              variant="contained"
              color="success"
              onClick={() => { setDetailOpen(false); setApproveOpen(true); }}
              sx={{ textTransform: 'none' }}
            >
              Onayla
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Onay */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ödemeyi Onayla</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ fontSize: '13px' }}>
              Havale onayı kaydı ödendi yapar; Logo fişi oluşturmaz.
            </Alert>
            <TextField
              type="datetime-local"
              label="Ödeme Tarihi"
              value={approvePaymentDate}
              onChange={(e) => setApprovePaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={textFieldSx}
            />
            <TextField
              label="Notlar"
              multiline
              rows={3}
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              fullWidth
              sx={textFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={processing}
            sx={{ textTransform: 'none', bgcolor: '#17C653' }}
          >
            {processing ? <CircularProgress size={18} color="inherit" /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İptal */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Proje Bağışını İptal Et</DialogTitle>
        <DialogContent>
          <TextField
            label="İptal nedeni"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            sx={{ mt: 1, ...textFieldSx }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={processing}
            sx={{ textTransform: 'none' }}
          >
            {processing ? <CircularProgress size={18} color="inherit" /> : 'İptal Et'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateProjectDonationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        projects={projects}
        onCreated={() => { loadRows(); loadStats(); }}
      />
    </Box>
  );
};

export default ProjectPayments;
